import { cn } from '@/lib/utils';
import type { AttachmentSummary } from '@/api/types';

interface AttachmentPreviewProps {
  attachment: AttachmentSummary;
  isOperator: boolean;
}

/**
 * Format file size to human readable string
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Get file icon based on MIME type
 */
function getFileIcon(fileType: string): string {
  if (fileType.startsWith('image/')) return '🖼️';
  if (fileType === 'application/pdf') return '📄';
  if (fileType.includes('word') || fileType.includes('document')) return '📝';
  if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '📊';
  if (fileType.startsWith('text/')) return '📃';
  return '📎';
}

/**
 * Render attachment preview in message bubble
 *
 * - Images: thumbnail with click-to-open
 * - Documents: icon + name + size + download link
 */
export function AttachmentPreview({ attachment, isOperator }: AttachmentPreviewProps) {
  const isImage = attachment.file_type.startsWith('image/');
  const fileSize = formatFileSize(attachment.file_size);
  const downloadUrl = attachment.download_url;

  if (!downloadUrl) {
    return (
      <div className={cn(
        'flex items-center gap-2 mt-2 px-3 py-2 rounded-lg text-sm',
        isOperator ? 'bg-blue-500/20 text-white' : 'bg-gray-200 text-gray-800'
      )}>
        <span className="text-lg">{getFileIcon(attachment.file_type)}</span>
        <p className="truncate">{attachment.file_name} ({fileSize})</p>
      </div>
    );
  }

  if (isImage) {
    return (
      <a
        href={downloadUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="block mt-2"
      >
        <img
          src={downloadUrl}
          alt={attachment.file_name}
          className="max-w-xs max-h-48 rounded-lg object-cover hover:opacity-90 transition-opacity"
          loading="lazy"
        />
      </a>
    );
  }

  return (
    <a
      href={downloadUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'flex items-center gap-2 mt-2 px-3 py-2 rounded-lg text-sm transition-colors',
        isOperator
          ? 'bg-blue-500/20 hover:bg-blue-500/30 text-white'
          : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
      )}
    >
      <span className="text-lg">{getFileIcon(attachment.file_type)}</span>
      <div className="flex-1 min-w-0">
        <p className="truncate font-medium">{attachment.file_name}</p>
        <p
          className={cn(
            'text-xs',
            isOperator ? 'text-blue-200' : 'text-gray-500'
          )}
        >
          {fileSize}
        </p>
      </div>
      <svg
        className={cn(
          'w-4 h-4 flex-shrink-0',
          isOperator ? 'text-blue-200' : 'text-gray-400'
        )}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
        />
      </svg>
    </a>
  );
}
